export const CHECKOUT_RETURN_CONSUMED_MESSAGE = 'ag-widget-return-consumed';

export interface CheckoutReturnParams {
  payment: string;
  intent: string;
}

const CHECKOUT_RETURN_PARAM_NAMES = ['payment', 'intent'] as const;

function paramsFrom(search: string | URLSearchParams | null | undefined): URLSearchParams {
  if (search instanceof URLSearchParams) return search;
  return new URLSearchParams(search || '');
}

function directCheckoutReturnParams(
  search: string | URLSearchParams | null | undefined,
): CheckoutReturnParams | null {
  const params = paramsFrom(search);
  const payment = (params.get('payment') || '').trim();
  const intent = (params.get('intent') || '').trim();
  if (!payment || !intent) return null;
  return { payment, intent };
}

/**
 * Return params can appear either directly on the iframe URL or nested inside
 * `parent_url` when the gateway returns to the embedding page and the loader
 * remounts the checkout iframe.
 */
export function readCheckoutReturnParams(
  search: string | URLSearchParams | null | undefined,
): CheckoutReturnParams | null {
  const direct = directCheckoutReturnParams(search);
  if (direct) return direct;

  const parentUrl = paramsFrom(search).get('parent_url');
  if (!parentUrl) return null;
  try {
    return directCheckoutReturnParams(new URL(parentUrl).search);
  } catch {
    return null;
  }
}

export function cleanCheckoutReturnParamsFromUrl(rawUrl: string): string {
  if (!rawUrl) return rawUrl;
  try {
    const url = new URL(rawUrl);
    for (const name of CHECKOUT_RETURN_PARAM_NAMES) {
      url.searchParams.delete(name);
    }
    return url.toString();
  } catch {
    return rawUrl;
  }
}

export function buildCheckoutIframeSearch(parentUrl: string, hostSearch: string): string {
  const params = new URLSearchParams();
  if (parentUrl) params.set('parent_url', cleanCheckoutReturnParamsFromUrl(parentUrl));

  const checkoutReturn = directCheckoutReturnParams(hostSearch);
  if (checkoutReturn) {
    params.set('payment', checkoutReturn.payment);
    params.set('intent', checkoutReturn.intent);
  }

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function cleanCheckoutReturnParamsFromCurrentUrl(win?: Window): void {
  const targetWindow = win ?? (typeof window !== 'undefined' ? window : null);
  if (!targetWindow) return;

  try {
    const url = new URL(targetWindow.location.href);
    const before = url.toString();
    for (const name of CHECKOUT_RETURN_PARAM_NAMES) {
      url.searchParams.delete(name);
    }
    const parentUrl = url.searchParams.get('parent_url');
    if (parentUrl) {
      url.searchParams.set('parent_url', cleanCheckoutReturnParamsFromUrl(parentUrl));
    }
    const after = url.toString();
    if (after !== before) {
      targetWindow.history.replaceState({}, '', after);
    }
  } catch {
    /* non-fatal */
  }
}

export function isCheckoutReturnConsumedMessage(data: unknown): boolean {
  return (
    !!data &&
    typeof data === 'object' &&
    (data as { type?: unknown }).type === CHECKOUT_RETURN_CONSUMED_MESSAGE
  );
}

export function checkoutReturnTargetOrigin(): string | null {
  if (typeof window === 'undefined') return null;
  const candidates = [
    new URLSearchParams(window.location.search).get('parent_url') || '',
    typeof document !== 'undefined' ? document.referrer : '',
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    try {
      return new URL(candidate).origin;
    } catch {
      /* try the next signal */
    }
  }
  return null;
}

export function postCheckoutReturnConsumed(): void {
  if (typeof window === 'undefined') return;
  try {
    if (!window.parent || window.parent === window) return;
    const targetOrigin = checkoutReturnTargetOrigin();
    if (!targetOrigin) return;
    window.parent.postMessage({ type: CHECKOUT_RETURN_CONSUMED_MESSAGE }, targetOrigin);
  } catch {
    /* non-fatal */
  }
}
